<script setup lang="ts">
import { Icon } from '@engage-web-ui/icon';
import { useDark } from '@vueuse/core';
import PrimeButton from 'primevue/button';
import PrimeDrawer from 'primevue/drawer';
import PrimePanel from 'primevue/panel';
import ImageAppDark from '../assets/images/app-dark.webp';
import ImageApp from '../assets/images/app.webp';
import VideoVisualisationDarkMp4 from '../assets/images/boards-dark.mp4?url';
import VideoVisualisationDarkWebm from '../assets/images/boards-dark.webm?url';
import VideoVisualisationMp4 from '../assets/images/boards.mp4?url';
import VideoVisualisationWebm from '../assets/images/boards.webm?url';
import VideoCollaborationMp4 from '../assets/images/products/collaborate.mp4?url';
import VideoCollaborationWebm from '../assets/images/products/collaborate.webm?url';
import ImageMonitoring from '../assets/images/products/live.webp';
import VideoDataStorageDarkMp4 from '../assets/images/upload-dark.mp4?url';
import VideoDataStorageDarkWebm from '../assets/images/upload-dark.webm?url';
import VideoDataStorageMp4 from '../assets/images/upload.mp4?url';
import VideoDataStorageWebm from '../assets/images/upload.webm?url';
import LogoDark from '../assets/images/vismo-dark.svg';
import Logo from '../assets/images/vismo.svg';

const runtimeConfig = useRuntimeConfig();

const isDarkMode = useDark();

const logo = computed(() => (unref(isDarkMode) ? LogoDark : Logo));
const appImage = computed(() => (unref(isDarkMode) ? ImageAppDark : ImageApp));

const drawerVisible = ref(false);

function onMenuClick() {
  drawerVisible.value = !drawerVisible.value;
}

const getStartedUrl = joinPath(runtimeConfig.public.idUrl, '/signup');

const {
  visible: productsDropdownVisible,
  forceHide: productsDropdownForceHide,
  onDropdownMouseOver: onProductsDropdownMouseOver,
  onDropdownMouseLeave: onProductsDropdownMouseLeave,
  onDropdownLinkClick: onProductsDropdownLinkClick,
} = useDropdown();

const {
  visible: solutionsDropdownVisible,
  forceHide: solutionsDropdownForceHide,
  onDropdownMouseOver: onSolutionsDropdownMouseOver,
  onDropdownMouseLeave: onSolutionsDropdownMouseLeave,
  onDropdownLinkClick: onSolutionsDropdownLinkClick,
} = useDropdown();

function useDropdown() {
  const visible = ref(false);
  const forceHide = ref(false);
  let timeout: NodeJS.Timeout | undefined;

  function onDropdownMouseOver() {
    if (timeout) {
      clearTimeout(timeout);
      timeout = undefined;
    }
    visible.value = true;
    forceHide.value = false;
  }

  function onDropdownMouseLeave() {
    if (timeout) {
      clearTimeout(timeout);
      timeout = undefined;
    }
    timeout = setTimeout(() => {
      visible.value = false;
      forceHide.value = false;
    }, 200);
  }

  function onDropdownLinkClick() {
    visible.value = false;
    forceHide.value = true;
  }

  return {
    visible,
    forceHide,
    onDropdownMouseOver,
    onDropdownMouseLeave,
    onDropdownLinkClick,
  };
}

function onDrawerLinkClick() {
  drawerVisible.value = false;
}

const productOver = ref<ProductType>('visualisation');
const dataStorageMp4 = computed(() =>
  unref(isDarkMode) ? VideoDataStorageDarkMp4 : VideoDataStorageMp4,
);
const dataStorageWebm = computed(() =>
  unref(isDarkMode) ? VideoDataStorageDarkWebm : VideoDataStorageWebm,
);
const visualisationMp4 = computed(() =>
  unref(isDarkMode) ? VideoVisualisationDarkMp4 : VideoVisualisationMp4,
);
const visualisationWebm = computed(() =>
  unref(isDarkMode) ? VideoVisualisationDarkWebm : VideoVisualisationWebm,
);

function onProductMouseOver(product: ProductType) {
  productOver.value = product;
}

// eslint-disable-next-line vue/max-len
const speakToUsLink = `mailto:${runtimeConfig.public.emailInfo}?subject=Solutions&body=Hi, I would love to talk about your solutions!`;
</script>

<template>
  <header class="header">
    <NuxtLink to="/"><img :src="logo" class="header__image" alt="Vismo Logo" /></NuxtLink>

    <nav>
      <ul class="header__navigation">
        <li class="header__navigation-item header__navigation-item--small-hide">
          <NuxtLink
            to="/products"
            style="position: relative"
            @mouseover="onProductsDropdownMouseOver"
            @mouseleave="onProductsDropdownMouseLeave"
            @click="onProductsDropdownLinkClick"
          >
            Products
            <span
              style="
                position: absolute;
                right: -12px;
                top: -10px;
                font-size: 0.7rem;
                color: red;
                transform: rotate(3deg);
              "
              >new</span
            >
          </NuxtLink>
          <PrimePanel
            class="header__navigation-dropdown"
            :class="{
              'header__navigation-dropdown--show': productsDropdownVisible,
              'header__navigation-dropdown--force-hide': productsDropdownForceHide,
            }"
            style="max-width: 60rem"
            :show-header="false"
          >
            <div class="header__navigation-dropdown-container">
              <div class="header__navigation-dropdown-cta">
                <video
                  v-show="productOver === 'data-storage'"
                  class="header__navigation-dropdown-cta-image"
                  style="aspect-ratio: 1/1"
                  autoplay
                  loop
                  muted
                  playsinline
                  preload="metadata"
                  aria-label="Storing and uploading AEC data in Vismo"
                >
                  <source :src="dataStorageMp4" type="video/mp4" />
                  <source :src="dataStorageWebm" type="video/webm" />
                </video>
                <video
                  v-show="productOver === 'collaboration'"
                  class="header__navigation-dropdown-cta-image"
                  style="aspect-ratio: 1/1"
                  autoplay
                  loop
                  muted
                  playsinline
                  preload="metadata"
                  aria-label="Live collaboration in Vismo with multiple users interacting on a 3D model"
                >
                  <source :src="VideoCollaborationMp4" type="video/mp4" />
                  <source :src="VideoCollaborationWebm" type="video/webm" />
                </video>
                <video
                  v-show="productOver === 'visualisation'"
                  class="header__navigation-dropdown-cta-image"
                  style="aspect-ratio: 1/1"
                  autoplay
                  loop
                  muted
                  playsinline
                  preload="metadata"
                  aria-label="Visualising 3D models in Vismo"
                >
                  <source :src="visualisationMp4" type="video/mp4" />
                  <source :src="visualisationWebm" type="video/webm" />
                </video>
                <img
                  v-show="productOver === 'monitoring'"
                  :src="ImageMonitoring"
                  class="header__navigation-dropdown-cta-image"
                  style="aspect-ratio: 1/1"
                  alt="Monitoring AEC data in Vismo"
                />
              </div>
              <ul class="header__navigation-dropdown-grid header__navigation-dropdown-grid--x2">
                <li
                  v-for="product in products"
                  :key="product.to"
                  class="header__navigation-dropdown-item"
                  @mouseover="onProductMouseOver(product.type)"
                  @pointerover="onProductMouseOver(product.type)"
                  @touchstart="onProductMouseOver(product.type)"
                  @mousedown="onProductMouseOver(product.type)"
                >
                  <NuxtLink
                    :to="product.to"
                    class="header__navigation-dropdown-item-link"
                    @click="onProductsDropdownLinkClick"
                  >
                    <Icon :icon="product.icon" class="header__navigation-dropdown-item-icon" />
                    <div class="header__navigation-dropdown-item-content">
                      <h5 class="header__navigation-dropdown-item-title">{{ product.name }}</h5>
                      <p class="header__navigation-dropdown-item-subtitle">
                        {{ product.description }}
                      </p>
                    </div>
                  </NuxtLink>
                </li>
              </ul>
            </div>
          </PrimePanel>
        </li>
        <li class="header__navigation-item header__navigation-item--small-hide">
          <NuxtLink
            to="/solutions"
            style="position: relative"
            @mouseover="onSolutionsDropdownMouseOver"
            @mouseleave="onSolutionsDropdownMouseLeave"
            @click="onSolutionsDropdownLinkClick"
          >
            Solutions
            <span
              style="
                position: absolute;
                right: -12px;
                top: -10px;
                font-size: 0.7rem;
                color: red;
                transform: rotate(3deg);
              "
              >new</span
            >
          </NuxtLink>
          <PrimePanel
            class="header__navigation-dropdown"
            :class="{
              'header__navigation-dropdown--show': solutionsDropdownVisible,
              'header__navigation-dropdown--force-hide': solutionsDropdownForceHide,
            }"
            :show-header="false"
          >
            <div class="header__navigation-dropdown-container">
              <ul class="header__navigation-dropdown-grid">
                <li
                  v-for="solution in solutions"
                  :key="solution.to"
                  class="header__navigation-dropdown-item"
                >
                  <NuxtLink
                    :to="solution.to"
                    class="header__navigation-dropdown-item-link"
                    @click="onSolutionsDropdownLinkClick"
                  >
                    <Icon :icon="solution.icon" class="header__navigation-dropdown-item-icon" />
                    <div class="header__navigation-dropdown-item-content">
                      <h5 class="header__navigation-dropdown-item-title">{{ solution.name }}</h5>
                      <p class="header__navigation-dropdown-item-subtitle">
                        {{ solution.description }}
                      </p>
                    </div>
                  </NuxtLink>
                </li>
              </ul>
              <NuxtLink :to="speakToUsLink" class="header__navigation-dropdown-cta">
                <img
                  :src="appImage"
                  class="header__navigation-dropdown-cta-image"
                  alt="Vismo application"
                />
                <PrimeButton
                  label="Speak To Us 🤙"
                  class="header__navigation-dropdown-cta-button"
                />
              </NuxtLink>
            </div>
          </PrimePanel>
        </li>
        <li class="header__navigation-item header__navigation-item--small-hide">
          <NuxtLink :to="runtimeConfig.public.announcementsUrl" target="_blank" external>
            What's New
          </NuxtLink>
        </li>
        <li class="header__navigation-item header__navigation-item--small-hide">
          <NuxtLink to="/pricing">Pricing</NuxtLink>
        </li>
        <li class="header__navigation-item header__navigation-item--small-hide">
          <NuxtLink to="/blog"> Blog </NuxtLink>
        </li>
        <li class="header__navigation-item header__navigation-item--small-hide">
          <NuxtLink :to="runtimeConfig.public.idUrl" external>
            <span class="header__navigation-item-text header__navigation-item-text--medium-hide"
              >Sign In</span
            >
            <Icon
              v-tooltip.bottom="'Sign In'"
              class="header__navigation-item-text header__navigation-item-text--medium-show"
              style="line-height: normal"
              icon="login"
            />
          </NuxtLink>
        </li>
        <li class="header__navigation-item header__navigation-item--small-hide">
          <NuxtLink :to="getStartedUrl" external>
            <PrimeButton v-tooltip.bottom="'Get Started For Free'">
              <span class="header__navigation-item-text header__navigation-item-text--medium-hide"
                >Get Started</span
              >
              <Icon
                class="header__navigation-item-text header__navigation-item-text--medium-show"
                icon="rocket_launch"
              />
            </PrimeButton>
          </NuxtLink>
        </li>
        <li class="header__navigation-item header__navigation-item--small-show">
          <PrimeButton @click="onMenuClick"><Icon icon="menu" /></PrimeButton>
        </li>
      </ul>

      <PrimeDrawer
        v-model:visible="drawerVisible"
        position="right"
        :show-close-icon="false"
        style="width: 15rem"
      >
        <nav>
          <ul>
            <li class="header__drawer-navigation-item">
              <NuxtLink to="/">Home</NuxtLink>
            </li>
            <li class="header__drawer-navigation-item">
              <NuxtLink to="/products" @click="onDrawerLinkClick">Products</NuxtLink>
            </li>
            <li class="header__drawer-navigation-item">
              <NuxtLink to="/solutions" @click="onDrawerLinkClick">Solutions</NuxtLink>
            </li>
            <li class="header__drawer-navigation-item">
              <NuxtLink
                :to="runtimeConfig.public.announcementsUrl"
                target="_blank"
                external
                @click="onDrawerLinkClick"
              >
                What's New
              </NuxtLink>
            </li>
            <li class="header__drawer-navigation-item">
              <NuxtLink to="/pricing" @click="onDrawerLinkClick">Pricing</NuxtLink>
            </li>
            <li class="header__drawer-navigation-item">
              <NuxtLink to="/blog" @click="onDrawerLinkClick">Blog</NuxtLink>
            </li>
            <li class="header__drawer-navigation-item header__drawer-navigation-item--break"></li>
            <li class="header__drawer-navigation-item">
              <NuxtLink :to="runtimeConfig.public.idUrl" external @click="onDrawerLinkClick">
                Sign In
              </NuxtLink>
            </li>
            <li class="header__drawer-navigation-item">
              <NuxtLink :to="getStartedUrl" external @click="onDrawerLinkClick">
                <PrimeButton label="Get Started" />
              </NuxtLink>
            </li>
          </ul>
        </nav>
      </PrimeDrawer>
    </nav>
  </header>
</template>

<style scoped lang="scss">
@use '../assets/styles/_globals.scss' as g;

.header {
  padding: 3rem 6rem;
  margin: 0 auto;
  max-width: 1400px;
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  position: relative;

  @media (max-width: g.$desktop-xl-breakpoint) {
    padding: 3rem 3rem;
    margin: 0 auto;
  }

  @media (max-width: g.$desktop-breakpoint) {
    padding: 2rem 3rem;
    margin: 0 auto;
  }

  @media (max-width: g.$tablet-breakpoint) {
    padding: 2rem 3rem;
    margin: 0 auto;
  }

  @media (max-width: g.$mobile-breakpoint) {
    padding: 1rem 2rem;
    margin: 0;
  }

  &__image {
    display: block;
    width: 10rem;

    @media (max-width: g.$mobile-breakpoint) {
      width: 8rem;
    }
  }

  &__navigation {
    display: flex;
    flex-direction: row;
    gap: 1rem;
    align-items: center;

    @media (min-width: g.$desktop-xl-breakpoint) {
      gap: 2rem;
    }
  }

  &__navigation-item {
    display: block;
    font-weight: 500;
    white-space: nowrap;

    &--small-hide {
      @media (max-width: g.$tablet-breakpoint) {
        display: none;
      }
    }

    &--small-show {
      @media (min-width: g.$tablet-breakpoint) {
        display: none;
      }
    }
  }

  &__navigation-item-text {
    &--medium-hide {
      @media (max-width: g.$desktop-breakpoint) {
        display: none;
      }
    }
    &--medium-show {
      @media (min-width: g.$desktop-breakpoint) {
        display: none;
      }
    }
  }

  &__navigation-dropdown {
    display: none;
    position: absolute;
    z-index: 10000;
    width: calc(100% - 2rem);
    max-width: 80rem;
    left: 50%;
    transform: translateX(-50%);
    opacity: 0;
    top: 8rem;
    box-shadow: 0 0.5rem 1rem 0
      color-mix(in srgb, light-dark(var(--p-surface-900), var(--p-surface-0)) 10%, transparent);

    --p-panel-header-padding: 0;
    --p-panel-content-padding: 1rem;

    @keyframes header-navigation-slide-up {
      0% {
        opacity: 0;
        transform: translateX(-50%) translateY(0);
      }
      100% {
        opacity: 1;
        transform: translateX(-50%) translateY(-1rem);
      }
    }

    &--show,
    &:hover {
      display: block;
      animation: header-navigation-slide-up 0.2s ease-in-out forwards;
    }

    &--force-hide {
      display: none !important;
    }

    @media (max-width: g.$desktop-xl-breakpoint) {
      max-width: 60rem;
      --p-panel-content-padding: 0;
    }

    @media (max-width: g.$desktop-breakpoint) {
      top: 7rem;
    }
  }

  &__navigation-dropdown-container {
    display: flex;
    flex-direction: row;
    gap: 0;
    align-items: center;
    justify-content: space-between;
  }

  &__navigation-dropdown-cta {
    position: relative;
    display: block;
    width: 100%;
    max-width: 25%;
    padding: 1rem;

    @media (max-width: 900px) {
      display: none;
    }
  }

  &__navigation-dropdown-cta-image {
    width: 100%;
    display: block;
    border-radius: 0.5rem;
    box-shadow: 0 0.5rem 1rem 0
      color-mix(in srgb, light-dark(var(--p-surface-900), var(--p-surface-0)) 10%, transparent);
  }

  &__navigation-dropdown-cta-button {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  &__navigation-dropdown-grid {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1rem;
    padding: 1rem;

    &--x2 {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: g.$desktop-xl-breakpoint) {
      gap: 0.5rem;
    }
  }

  &__navigation-dropdown-item {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.5rem;
    border-radius: 0.5rem;
    text-decoration: none;
  }

  &__navigation-dropdown-item-link {
    display: flex;
    flex-direction: row;
    gap: 1rem;
  }

  &__navigation-dropdown-item-icon {
    font-size: 1.2rem;
    color: var(--p-text-color);
    background: light-dark(var(--p-surface-100), var(--p-surface-900));
    border-radius: 50%;
    width: 2.5rem;
    height: 2.5rem;
    text-align: center;
    line-height: 2.5rem;
    min-width: 2.5rem;
  }

  &__navigation-dropdown-item-content {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  &__navigation-dropdown-item-title {
    font-size: 1rem;
    font-weight: 600;
  }

  &__navigation-dropdown-item-subtitle {
    font-size: 0.8rem;
    font-weight: 400;
    color: var(--p-text-muted-color);
    text-wrap: auto;
  }

  &__drawer-navigation-item {
    display: block;
    font-weight: 500;
    white-space: nowrap;
    padding: 0.5rem 1rem;
    text-align: right;

    &--break {
      border-top: 1px solid var(--p-content-border-color);
      margin: 0.5rem 0 -0.5rem;
    }
  }
}
</style>
